/* eslint-disable react-hooks/exhaustive-deps */
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { LuLink } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

const CaseDedupListTable = ({ data }) => {
  const navigate = useNavigate();

  const handleSelectedRecord = (clickedData) => {
    if (clickedData?.id) {
      navigate(`/admin/caseDedup/${clickedData?.id}/${clickedData?.PERSON_ID}`);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "FIRST_NAME", header: "First Name" },
      { accessorKey: "LAST_NAME", header: "Last Name" },
      { accessorKey: "PRODUCT", header: "Product" },
      {
        accessorKey: "BIRTH_DATE",
        header: "Birth Date",
        cell: ({ getValue }) =>
          moment(getValue(), "YYYYMMDD").format("MM/DD/YYYY"),
      },
      { accessorKey: "GENDER", header: "Gender" },
      {
        accessorKey: "CREATE_DATE",
        header: "Create Date",
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? moment(value, "YYYYMMDD").format("MM/DD/YYYY") : "-";
        },
      },
      {
        accessorKey: "_ts",
        header: "Update Date",
        cell: ({ getValue }) =>
          moment.unix(getValue()).format("MM/DD/YYYY hh:mm A"),
      },
      {
        accessorKey: "MERGE",
        header: "Merge",
        enableSorting: false, // disable sort for action column
        cell: ({ row }) => {
          return (
            <IconButton
              icon={<LuLink />}
              onClick={() => handleSelectedRecord(row?.original)}
              aria-label="Merge"
              size="sm"
            />
          );
        },
      },
    ],
    []
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(7);
  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize })
          : updater;
      setPageIndex(newPagination.pageIndex);
      setPageSize(newPagination.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box maxW="75vw" mx="auto">
      <TableContainer h="556px" border="1px solid #3c1514" overflowY="scroll">
        <Table variant="simple">
          <Thead>
            <Tr>
              {table.getFlatHeaders().map((header) => (
                <Th
                  key={header.id}
                  cursor={header.column.getCanSort() ? "pointer" : "default"}
                  onClick={header.column.getToggleSortingHandler()}
                  userSelect="none"
                >
                  <Flex align="center" gap={1}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <>
                        {header.column.getIsSorted() === "asc" && " 🔼"}
                        {header.column.getIsSorted() === "desc" && " 🔽"}
                        {!header.column.getIsSorted() && " ↕️"}
                      </>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Flex justify="space-between" align="center" mt={2}>
        <Button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          leftIcon={<ArrowLeftIcon />}
          _hover={{
            background: "#3c1514",
            color: "white",
          }}
          size="sm"
        >
          Previous
        </Button>
        <Text>
          Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
          {table.getPageCount()}
        </Text>
        <Button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          rightIcon={<ArrowRightIcon />}
          _hover={{
            background: "#3c1514",
            color: "white",
          }}
          size="sm"
        >
          Next
        </Button>
      </Flex>
    </Box>
  );
};

export default CaseDedupListTable;
